import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import { Product } from 'reactApp/types/Billing';

import plural from '../../public/js/lib/plural';

const MAP_PERIODS = {
    d: 'дня',
    m: 'месяца',
    w: 'недели',
    y: 'года',
};

const MAP_NUMBERS = {
    1: ['первого', 'первой'],
    2: ['второго', 'второй'],
    3: ['третьего', 'третьей'],
    4: ['четвертого', 'четвертой'],
    5: ['пятого', 'пятой'],
    6: ['шестого', 'шестой'],
    7: ['седьмого', 'седьмой'],
    8: ['восьмого', 'восьмой'],
    9: ['девятого', 'девятой'],
    10: ['десятого', 'десятой'],
    11: ['одиннадцатого', 'одиннадцатой'],
};

export const parsePeriod = (period: string) => period && period.match(/(^\d*)+(\D)$/);

export const simplifyPeriod = (count, name) => {
    let resultCount = count;
    let resultName = name;

    if (count === 7 && name === 'd') {
        resultCount = 1;
        resultName = 'w';
    }

    if (count % 12 === 0 && name === 'm') {
        resultCount = count / 12;
        resultName = 'y';
    }

    return [resultCount, resultName] as const;
};

export const getPeriodName = (period: string, showOne = false, genetive = false, skipSimplify = false): string => {
    if (!period) {
        return '';
    }

    const periodMatches = parsePeriod(period);

    if (!periodMatches) {
        return '';
    }

    let count = Number(periodMatches[1]);
    let name = periodMatches[2];
    const text = {
        d: ['день', 'дня', 'дней'],
        w: [genetive ? 'неделю' : 'неделя', 'недели', 'недель'],
        m: ['месяц', 'месяца', 'месяцев'],
        y: ['год', 'года', 'лет'],
    };

    if (!count || !name) {
        return '';
    }

    [count, name] = skipSimplify ? [count, name] : simplifyPeriod(count, name);

    return `${(count === 1 && !showOne) || (count === 6 && name === 'm') ? '' : `${count}\u00A0`}${
        count === 6 && name === 'm' ? 'полгода' : text[name][plural(count)]
    }`;
};

export const isYearPeriod = (period: string): boolean => {
    const periodMatches = parsePeriod(period);
    if (!periodMatches) {
        return false;
    }

    const count = Number(periodMatches[1]);
    let name = periodMatches[2];

    if (count % 12 === 0 && name === 'm') {
        name = 'y';
    }

    return name === 'y';
};

export const isMonthPeriod = (period: string): boolean => period === '1m';
export const isMonth3Period = (period: string): boolean => period === '3m';
export const isMonth6Period = (period: string): boolean => period === '6m';

export const getMonthes = (period: string): number => {
    const periodMatches = parsePeriod(period);
    if (!periodMatches) {
        return 0;
    }

    let count = Number(periodMatches[1]);
    const name = periodMatches[2];

    if (name === 'y') {
        count *= 12;
    } else if (name !== 'm') {
        return 0;
    }

    return count;
};

export const getYearPrice = (product?: Product): number | null => {
    if (!product) {
        return null;
    }
    const koeff = isYearPeriod(product.period) ? 1 : isMonth3Period(product.period) ? 4 : 12;
    return koeff * product.price;
};

export const getTariffProductsByPeriods = (products: Product[], show6Month = false): { yearProduct: Product; monthProduct: Product } => {
    const yearProduct = products.filter((product): boolean => isYearPeriod(product.period))[0];
    const monthProduct = products.filter((product): boolean =>
        show6Month ? isMonth6Period(product.period) : isMonthPeriod(product.period)
    )[0];

    return {
        yearProduct,
        monthProduct,
    };
};

export const getTrialDateEnd = (trialPeriod, startDate = new Date()) => {
    let method = addYears;
    switch (trialPeriod.slice(-1)) {
        case 'm':
            method = addMonths;
            break;
        case 'd':
            method = addDays;
            break;
        default:
            break;
    }
    return method(startDate, parseInt(trialPeriod));
};

export const getTypeOfPeriodName = (period: string) => {
    const periodMatches = parsePeriod(period);
    if (!periodMatches) {
        return '';
    }

    const names = {
        d: 'день',
        w: 'неделю',
        m: 'месяц',
        y: 'год',
    };

    return names[periodMatches[2]];
};

export const getPeriodNameAsWord = (period, next = false) => {
    const periodMatches = parsePeriod(period);
    if (!periodMatches) {
        return '';
    }
    let count = Number(periodMatches[1]);
    let name = periodMatches[2];

    if (!count || !name) {
        return '';
    }

    [count, name] = simplifyPeriod(count, name);

    if (next) {
        count = count + 1;
    }

    return `${MAP_NUMBERS?.[count]?.[name !== 'w' ? 0 : 1] || count} ${MAP_PERIODS[name]}`;
};

export const nowIsTrialPeriod = (trialPeriod, startDate) => {
    if (!trialPeriod) {
        return false;
    }

    const trialEnd = getTrialDateEnd(trialPeriod, startDate);
    const now = new Date();

    return now < trialEnd;
};
